import React from 'react';
import { VIEW } from './siteLayout';
import { THEMES } from './siteThemes';
import './SiteMap.css';

/**
 * The site as a PLAN, drawn in its theme (siteThemes.jsx: blueprint floor plan, warehouse site plan
 * or region map). Missions are small numbered markers on their room/town - red and pulsing: ready,
 * green tick: done, grey padlock: later in the story, hollow: not built yet (a legend explains them);
 * the details live in the player's side panel, not on the drawing. Places the player can go to glow;
 * "you" is a marker. `zoom` (a place id) pushes in on that place before its mission. Without
 * `onRoom` it's a static preview (the editor). All shapes are drawn - no emoji inside the SVG.
 */
export function roomState(room, missions) {
  const states = room.missions.map((id) => missions[id]?.status || 'unready');
  if (states.some((s) => s === 'next' || s === 'open')) return 'go';
  if (states.length && states.every((s) => s === 'done' || s === 'unready') && states.includes('done')) return 'done';
  return 'idle';
}

function Pin({ x, y, id, status }) {
  const n = String(id).replace(/^m/i, '');
  return (
    <g className={`sm-pin is-${status}`} transform={`translate(${x} ${y})`}>
      {(status === 'next' || status === 'open') && <circle r="17" className="sm-pin-pulse" />}
      <circle r="12" className="sm-pin-dot" />
      {status === 'done' && <path d="M-5 0.5 L-1.5 4 L5.5 -4.5" className="sm-pin-glyph" />}
      {status === 'locked' && (
        <g className="sm-pin-lock"><rect x="-4.5" y="-1" width="9" height="7" rx="1.2" /><path d="M-2.8 -1 V-3.4 a2.8 2.8 0 0 1 5.6 0 V-1" /></g>
      )}
      {(status === 'next' || status === 'open' || status === 'unready') && <text y="4.3" textAnchor="middle" className="sm-pin-num">{n}</text>}
    </g>
  );
}

const LEGEND = [['open', 'Ready'], ['done', 'Done'], ['locked', 'Later'], ['unready', 'Not built']];

export default function SiteMap({ layout, missions, marker = null, selected = null, zoom = null, onRoom = null, title = '' }) {
  const theme = THEMES[layout.theme] || THEMES.floor;
  const zoomRoom = zoom && layout.rooms.find((r) => r.id === zoom);
  const scale = zoomRoom ? Math.min(VIEW.w / zoomRoom.w, VIEW.h / zoomRoom.h) : 1;
  const zoomStyle = zoomRoom ? {
    transform: `translate(${VIEW.w / 2}px, ${VIEW.h / 2}px) scale(${scale}) translate(${-(zoomRoom.x + zoomRoom.w / 2)}px, ${-(zoomRoom.y + zoomRoom.h / 2)}px)`
  } : undefined;
  const present = new Set(Object.values(missions).map((m) => m.status === 'next' ? 'open' : m.status));
  const legend = ({ x, y, row = false, className }) => (
    <g className={`sm-legend ${className}`} transform={`translate(${x} ${y})`}>
      {LEGEND.filter(([s]) => present.has(s)).map(([s, label], i) => (
        <g key={s} transform={`translate(${i * (row ? 82 : 86)} 0)`}>
          <Pin x={0} y={0} id="" status={s} />
          <text x="18" y="4.5" className="sm-legend-text">{label}</text>
        </g>
      ))}
    </g>
  );

  return (
    <svg className={`sm-svg sm-theme-${layout.theme} ${onRoom ? 'is-live' : ''}`} viewBox={`0 0 ${VIEW.w} ${VIEW.h}`} role="group" aria-label={title || 'Site plan'}>
      <defs><theme.Defs /></defs>
      <theme.Backdrop />
      <g className="sm-world" style={zoomStyle}>
        <theme.Ground layout={layout} />
        {layout.rooms.map((room, index) => {
          const state = roomState(room, missions);
          const live = onRoom && state === 'go';
          const at = theme.pinsAt(room);
          return (
            <g key={room.id} className={`sm-room is-${state} ${selected === room.id ? 'is-selected' : ''}`}
              {...(onRoom ? {
                role: 'button', tabIndex: 0,
                'aria-label': `${room.name}: ${room.missions.map((id) => `${missions[id]?.name || id} (${missions[id]?.status || 'not built'})`).join(', ')}`,
                onClick: () => onRoom(room.id),
                onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRoom(room.id); } }
              } : {})}>
              {live && <theme.Glow room={room} />}
              <theme.Place room={room} index={index} />
              {room.missions.slice(0, 7).map((id, i) => <Pin key={id} x={at.x + i * 28} y={at.y} id={id} status={missions[id]?.status || 'unready'} />)}
              <rect x={room.x} y={room.y} width={room.w} height={room.h} className="sm-hit" />
            </g>
          );
        })}
        {marker && (
          <g className="sm-you" transform={`translate(${marker.x} ${marker.y})`} aria-label="You are here">
            <circle r="13" className="sm-you-ring" />
            <circle r="7" className="sm-you-dot" />
            <text y="-19" textAnchor="middle" className="sm-you-label">YOU</text>
          </g>
        )}
      </g>
      {!zoomRoom && <theme.Overlay title={title} legend={legend} />}
    </svg>
  );
}
